import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { EditAppointmentTypeDialog } from '@/components/admin/EditAppointmentTypeDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Calendar, Edit, Loader2, Plus, Search, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/appointment-types')({
  component: AppointmentTypesPage,
});

// Using the canonical AppointmentType from ehr-gateway with additional id field for UI
interface AppointmentType {
  id: string;
  ehrId: string;
  name: string;
  abbreviation: string;
  durationCategory: 'short' | 'medium' | 'long' | 'other';
  ehrSystem?: string;
  organizationId?: string;
}

function AppointmentTypesPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State
  const [selectedEhrSystem, setSelectedEhrSystem] = useState<'imedicware'>('imedicware');
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    appointmentType: AppointmentType | null;
    isNew: boolean;
  }>({
    isOpen: false,
    appointmentType: null,
    isNew: false,
  });

  // Use current organization ID from Clerk
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries
  const appointmentTypesQuery = trpc.admin.getAppointmentTypes.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  // tRPC mutations
  const createAppointmentTypeMutation = trpc.admin.createAppointmentType.useMutation({
    onSuccess: () => {
      toast.success('Appointment type created successfully');
      appointmentTypesQuery.refetch();
      setEditDialog({ isOpen: false, appointmentType: null, isNew: false });
    },
    onError: (error) => {
      toast.error(`Failed to create appointment type: ${error.message}`);
    },
  });

  const updateAppointmentTypeMutation = trpc.admin.updateAppointmentType.useMutation({
    onSuccess: () => {
      toast.success('Appointment type updated successfully');
      appointmentTypesQuery.refetch();
      setEditDialog({ isOpen: false, appointmentType: null, isNew: false });
    },
    onError: (error) => {
      toast.error(`Failed to update appointment type: ${error.message}`);
    },
  });

  const handleGoBack = () => {
    router.navigate({ to: '/admin' });
  };

  const handleOpenEditDialog = (appointmentType: AppointmentType | null, isNew: boolean) => {
    setEditDialog({
      isOpen: true,
      appointmentType,
      isNew,
    });
  };

  const handleSaveAppointmentType = (appointmentType: AppointmentType) => {
    if (!currentOrganizationId) {
      toast.error('Cannot save appointment type without organization ID');
      return;
    }

    if (editDialog.isNew) {
      // Create new appointment type
      createAppointmentTypeMutation.mutate({
        organizationId: currentOrganizationId,
        ehrSystem: selectedEhrSystem,
        ehrId: appointmentType.ehrId,
        name: appointmentType.name,
        abbreviation: appointmentType.abbreviation,
        durationCategory: appointmentType.durationCategory,
      });
    } else {
      // Update existing appointment type
      updateAppointmentTypeMutation.mutate({
        id: appointmentType.id,
        name: appointmentType.name,
        abbreviation: appointmentType.abbreviation,
        durationCategory: appointmentType.durationCategory,
      });
    }
  };

  // Filter appointment types based on search
  const filteredAppointmentTypes = (appointmentTypesQuery.data?.appointmentTypes || []).filter(
    (type) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        type.name.toLowerCase().includes(searchLower) ||
        type.abbreviation.toLowerCase().includes(searchLower) ||
        type.ehrId.toLowerCase().includes(searchLower)
      );
    }
  );

  // Loading state
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied for non-super admins
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <ShieldAlert className="h-5 w-5" />
                Access Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                This page is only available to super administrators. Please contact your
                administrator if you need access.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Appointment Types"
      description="Manage appointment types and duration settings"
      icon={Calendar}
      isSuperAdminPage={true}
    >
      {/* Organization & EHR System Info */}
      <Card>
        <CardHeader>
          <CardTitle>Organization & EHR System</CardTitle>
          <CardDescription>Managing appointment types for the current organization</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <div className="flex-1">
            <div className="text-sm font-medium text-muted-foreground mb-1">Organization</div>
            <div className="text-lg font-semibold">
              {organization?.name ?? 'Unknown Organization'}
            </div>
          </div>
          <div className="w-48">
            <div className="text-sm font-medium text-muted-foreground mb-1">EHR System</div>
            <Select
              value={selectedEhrSystem}
              onValueChange={(value) => setSelectedEhrSystem(value as 'imedicware')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="imedicware">iMedicWare</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      {currentOrganizationId ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Appointment Types</CardTitle>
                <CardDescription>
                  {filteredAppointmentTypes.length} appointment type(s) configured
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search appointment types..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
                <Button
                  onClick={() => handleOpenEditDialog(null, true)}
                  disabled={!currentOrganizationId}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {appointmentTypesQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredAppointmentTypes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm
                  ? 'No appointment types found matching your search.'
                  : 'No appointment types configured yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">EHR ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-32">Abbreviation</TableHead>
                      <TableHead className="w-28">Duration</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAppointmentTypes.map((appointmentType) => (
                      <TableRow key={appointmentType.id}>
                        <TableCell className="font-mono text-sm">{appointmentType.ehrId}</TableCell>
                        <TableCell className="font-medium">{appointmentType.name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {appointmentType.abbreviation}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              appointmentType.durationCategory === 'short'
                                ? 'bg-blue-100 text-blue-700'
                                : appointmentType.durationCategory === 'medium'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : appointmentType.durationCategory === 'long'
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {appointmentType.durationCategory}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditDialog(appointmentType, false)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              Please select an organization to manage appointment types.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      {editDialog.isOpen && (
        <EditAppointmentTypeDialog
          isOpen={editDialog.isOpen}
          onClose={() => setEditDialog({ isOpen: false, appointmentType: null, isNew: false })}
          onSave={handleSaveAppointmentType}
          appointmentType={editDialog.appointmentType}
          isNew={editDialog.isNew}
          isLoading={
            createAppointmentTypeMutation.isPending || updateAppointmentTypeMutation.isPending
          }
        />
      )}
    </AdminPageLayout>
  );
}
